/* ==========================================================================
   CADOO INDIVIDUAL GIFT WRAPPING - cart relationship engine
   --------------------------------------------------------------------------
   One wrappable product cart line may own exactly one "Individual Gift
   Wrapping" add-on line. The relationship is carried by a stable private
   property that lives on BOTH lines:

     product line      _cadoo_wrap_group_id : "cdw-xxxxxxxx-xxxx"
     wrapping line     _cadoo_wrap_group_id : "cdw-xxxxxxxx-xxxx"   (same value)
                       Wrapping for         : "Title — Variant"      (public)

   The group id - not the title, not the variant id, not the cart position,
   not the current line key - is the permanent relationship. A line key is
   derived by Shopify from variant + properties, so it changes the moment a
   property is written; the group id survives that, survives a refresh, and
   keeps two cart lines of the same variant independently identifiable.

   The wrapping line's QUANTITY is the number of units to wrap. The customer's
   INTENT is recorded beside it, on the wrapping line only:

     wrapping line     _cadoo_wrap_scope    : "all" | "units"      (private)

     all    the customer wrapped the whole product line. When that line's
            quantity is edited on a checkout step (editLine below), the
            wrapping follows it: W = the newly accepted Q.
     units  the customer chose a number of units smaller than the line.
            Edits keep that choice and only cap it: W = min(W, Q). Added
            units are never wrapped (or charged) automatically.

   Lines written before the scope existed carry none; for them "all" is
   inferred when W >= Q, otherwise "units" - and the inferred value is
   written explicitly the first time an edit touches the line, so a partial
   line reduced to W = Q does not later read as "wrap everything".
   Unwrapped lines never gain wrapping, and a selection the customer cleared
   leaves no wrapping line behind, so nothing can be resurrected.

   Every operation here is queued on a single promise chain, so repeated
   taps, slow requests and retries can never interleave and can never create
   a duplicate wrapping charge.
   ========================================================================== */
(function () {
  'use strict';

  var GROUP_KEY = '_cadoo_wrap_group_id';
  var LABEL_KEY = 'Wrapping for';
  var SCOPE_KEY = '_cadoo_wrap_scope';

  /* ---------- cart endpoints ---------- */
  function ajax(url) { return /\.js(?:\?|$)/.test(url) ? url : url + '.js'; }

  function routes() {
    var r = (window.CadooConfig && window.CadooConfig.routes) || {};
    return {
      cart: r.cart || '/cart',
      add: ajax(r.cart_add || '/cart/add'),
      change: ajax(r.cart_change || '/cart/change')
    };
  }

  /* Every request is bounded. A request that never answers (a stalled mobile
     connection, a tab that was backgrounded mid-request) would otherwise hold
     the queue below for ever, and with it every later bag edit and the
     Delivery step's Continue ("SAVING..."). On timeout the request is
     aborted and fails like a network error; callers then re-read the cart
     before anything is retried, and every write here is absolute (a
     quantity, never "+1"), so a retry cannot add a second charge. */
  var REQUEST_TIMEOUT = 15000;
  function timedFetch(url, opts, read) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, REQUEST_TIMEOUT) : null;
    if (ctrl) opts.signal = ctrl.signal;
    function done() { if (timer) clearTimeout(timer); }
    return fetch(url, opts).then(read).then(function (v) { done(); return v; }, function (e) { done(); throw e; });
  }

  function post(url, body) {
    return timedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }, function (r) {
      if (!r.ok) throw new Error('cart ' + r.status);
      return r.json();
    });
  }

  function getCart() {
    return timedFetch(routes().cart + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }, function (r) {
      if (!r.ok) throw new Error('cart ' + r.status);
      return r.json();
    });
  }

  /* ---------- ids ----------
     When a line is given its first group id, its key changes. Anything still
     holding the old key - a selection captured before that write, a queued
     second request from an impatient tap, a row the page rendered before the
     id existed - would otherwise point at a line that no longer exists and
     read as "wrap nothing". Every key that has been exchanged for a group id
     is therefore remembered here for the life of the page, and a stale
     reference is resolved through it.
   */
  var keyAlias = {};

  function canonical(wanted) {
    var out = {};
    Object.keys(wanted || {}).forEach(function (k) {
      out[keyAlias[k] || k] = wanted[k];
    });
    return out;
  }

  function newGroupId() {
    var rand;
    if (window.crypto && window.crypto.getRandomValues) {
      var buf = new Uint32Array(2);
      window.crypto.getRandomValues(buf);
      rand = buf[0].toString(36) + buf[1].toString(36);
    } else {
      rand = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
    return 'cdw-' + Date.now().toString(36) + '-' + rand.slice(0, 10);
  }

  /* A cart payload, as opposed to the line-item payload /cart/add.js returns.
     Used to decide whether the last write already handed back the authoritative
     state or whether one read is genuinely required. */
  function isCart(v) {
    return !!(v && typeof v === 'object' && Array.isArray(v.items) && typeof v.item_count === 'number');
  }

  /* ---------- line helpers ---------- */
  function props(line) {
    var p = line && line.properties;
    return p && typeof p === 'object' ? p : {};
  }

  /* Merge a private property into the line's COMPLETE existing property set.
     /cart/change.js replaces the properties object wholesale, so anything not
     repeated here would be destroyed - personalisation included. */
  function mergeProps(line, extra) {
    var out = {};
    var existing = props(line);
    Object.keys(existing).forEach(function (k) {
      var v = existing[k];
      if (v === null || v === undefined) return;
      out[k] = v;
    });
    Object.keys(extra).forEach(function (k) { out[k] = extra[k]; });
    return out;
  }

  function isWrapLine(line, cfg) {
    return line.handle === cfg.wrapHandle;
  }

  function isExcludedHandle(line, cfg) {
    return line.handle === cfg.wrapHandle || line.handle === cfg.premiumHandle;
  }

  /* A product line may carry wrapping only when the merchant has explicitly
     marked the product eligible (custom.individual_wrap_eligible). The map is
     rendered by Liquid from the products currently in the cart; anything not
     marked true there is never given wrapping, and eligibility is never
     guessed from a title or a handle. */
  function isEligible(line, cfg) {
    return eligibility(line, cfg) === true;
  }

  /* true / false when the page knows the merchant's answer for this product,
     null when it does not - a product added (in another tab, say) after the
     page rendered its eligibility map. A sweep never strips wrapping on the
     strength of "unknown": only an explicit false, or no product line at
     all, makes a wrapping charge an orphan. */
  function eligibility(line, cfg) {
    if (isExcludedHandle(line, cfg)) return false;
    if (props(line)._cadoo_gift_addon) return false;
    var v = cfg.eligible[String(line.product_id)];
    if (v === true) return true;
    if (v === false) return false;
    return null;
  }

  function wrapLabel(line) {
    var title = line.product_title || line.title || '';
    var variant = line.variant_title;
    if (variant && variant !== 'Default Title') return title + ' — ' + variant;
    return title;
  }

  /* The customer's wrapping intent for a product line - see the header. */
  function scopeOf(wrapLine, productLine) {
    var s = props(wrapLine)[SCOPE_KEY];
    if (s === 'all' || s === 'units') return s;
    return productLine && wrapLine.quantity >= productLine.quantity ? 'all' : 'units';
  }

  function findByGroup(items, groupId, cfg, wantWrap) {
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (props(it)[GROUP_KEY] !== groupId) continue;
      if (wantWrap && !isWrapLine(it, cfg)) continue;
      if (!wantWrap && isWrapLine(it, cfg)) continue;
      return it;
    }
    return null;
  }

  /* ---------- public read model ---------- */
  /* Returns the eligible product lines and, for each, the wrapping quantity
     currently committed in the Shopify cart. This is the ONLY source the UI
     restores from, so a refresh or a backward navigation always reflects the
     real cart rather than anything remembered in the browser. */
  function readModel(cart, cfg) {
    var items = (cart && cart.items) || [];
    var lines = [];
    items.forEach(function (it, index) {
      if (!isEligible(it, cfg)) return;
      var groupId = props(it)[GROUP_KEY] || null;
      var wrapped = 0;
      var scope = null;
      if (groupId) {
        var w = findByGroup(items, groupId, cfg, true);
        if (w) { wrapped = Math.min(w.quantity, it.quantity); scope = scopeOf(w, it); }
      }
      lines.push({
        index: index,
        key: it.key,
        groupId: groupId,
        productId: it.product_id,
        variantId: it.variant_id,
        title: it.product_title || it.title,
        variantTitle: it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '',
        image: it.image || null,
        quantity: it.quantity,
        linePrice: it.original_line_price,
        wrapQuantity: wrapped,
        wrapScope: scope
      });
    });
    return lines;
  }

  /* ---------- reconcile ---------- */
  /* desired: { <groupIdOrLineKey>: units }  - units is what the customer asked
     for. Anything absent is treated as zero.
     mode 'apply'   - the customer's selection is authoritative.
     mode 'cleanup' - keep whatever wrapping the cart already holds, but strip
                      orphans / ineligible lines and cap to the product line
                      quantity. Used on the delivery step, before checkout. */
  function reconcile(desired, cfg, mode) {
    /* The selection may be handed over as a function, so a request that has
       been waiting its turn in the queue is read at the moment it actually
       runs rather than at the moment it was asked for. */
    var wanted = canonical(typeof desired === 'function' ? desired() : (desired || {}));

    return getCart().then(function (cart) {
      /* --- phase 1: give every line the customer wants wrapped a stable id ---
         Written one line at a time, re-reading the cart after each write,
         because changing a line's properties changes that line's key. A line
         that already has an id keeps it - ids are never regenerated. */
      var chain = Promise.resolve(cart);

      if (mode !== 'cleanup') {
        (cart.items || []).forEach(function (line) {
          if (!isEligible(line, cfg)) return;
          if (props(line)[GROUP_KEY]) return;
          var units = Number(wanted[line.key] || 0);
          if (units <= 0) return;

          chain = chain.then(function (c) {
            /* Re-resolve the line in the freshest cart by its key. Writing to
               one line never alters another line's key, but re-resolving keeps
               this correct even if the cart changed underneath us. */
            var fresh = (c.items || []).filter(function (x) { return x.key === line.key; })[0];
            if (!fresh) return c;
            var groupId = newGroupId();
            /* Carry the customer's request across to the new id, and record
               the exchange so any later request still naming the old key
               resolves to this line rather than to nothing. */
            wanted[groupId] = units;
            keyAlias[line.key] = groupId;
            return post(routes().change, {
              id: fresh.key,
              quantity: fresh.quantity,
              properties: mergeProps(fresh, (function () { var o = {}; o[GROUP_KEY] = groupId; return o; })())
            });
          });
        });
      }

      /* The /cart/change.js response IS the updated cart, so the chain already
         carries the freshest copy. Re-fetching here was a whole extra round
         trip on every single selection, for data the browser already had. */
      return chain;
    }).then(function (cart) {
      /* --- phase 2: reconcile the wrapping lines that exist --- */
      var items = (cart.items || []).slice();
      var ops = [];
      var seen = {};

      items.forEach(function (line) {
        if (!isWrapLine(line, cfg)) return;
        var groupId = props(line)[GROUP_KEY];
        var target = groupId ? findByGroup(items, groupId, cfg, false) : null;

        /* Orphaned (no id at all, or no surviving product line), a duplicate
           for the same group, or attached to a line that is not eligible for
           paid wrapping - none of these may reach checkout. */
        var known = target ? eligibility(target, cfg) : false;
        if (!groupId || !target || known === false || seen[groupId]) {
          ops.push({ type: 'remove', key: line.key });
          return;
        }
        seen[groupId] = true;

        /* The customer's selection only speaks for the rows this page drew
           (their ids are in `wanted`). Wrapping on any other line - one the
           page has never seen, or whose eligibility it cannot vouch for - is
           kept as the cart holds it, capped to its product line. */
        var speaks = mode !== 'cleanup' && known === true &&
          Object.prototype.hasOwnProperty.call(wanted, groupId);
        var units = speaks ? Number(wanted[groupId] || 0) : line.quantity;
        units = Math.max(0, Math.min(units, target.quantity));

        if (units === 0) {
          ops.push({ type: 'remove', key: line.key });
          return;
        }

        var label = wrapLabel(target);
        /* When the customer's selection speaks for this line, record what it
           means: every unit of the line ("all") or a smaller number ("units").
           Otherwise the stored intent is left exactly as it is. */
        var scope = speaks ? (units >= target.quantity ? 'all' : 'units') : null;
        if (line.quantity !== units || props(line)[LABEL_KEY] !== label ||
            (scope && props(line)[SCOPE_KEY] !== scope)) {
          var p = mergeProps(line, (function () {
            var o = {}; o[GROUP_KEY] = groupId; o[LABEL_KEY] = label;
            if (scope) o[SCOPE_KEY] = scope;
            return o;
          })());
          ops.push({ type: 'change', key: line.key, quantity: units, properties: p });
        }
      });

      /* --- phase 3: add the wrapping lines that are missing --- */
      if (mode !== 'cleanup') {
        items.forEach(function (line) {
          if (!isEligible(line, cfg)) return;
          var groupId = props(line)[GROUP_KEY];
          if (!groupId || seen[groupId]) return;
          var units = Math.max(0, Math.min(Number(wanted[groupId] || 0), line.quantity));
          if (units === 0) return;
          seen[groupId] = true;
          var p = {};
          p[GROUP_KEY] = groupId;
          p[LABEL_KEY] = wrapLabel(line);
          p[SCOPE_KEY] = units >= line.quantity ? 'all' : 'units';
          ops.push({ type: 'add', id: cfg.wrapVariantId, quantity: units, properties: p });
        });
      }

      /* Run strictly in sequence. The next operation is issued only once the
         previous one has been accepted by Shopify, so no two writes can race
         into a duplicate charge.

         /cart/change.js answers with the whole updated cart, so the last such
         answer IS the authoritative post-write state and no separate read is
         needed. /cart/add.js answers with the added line items only, so after
         an add - and only then - the cart is read once. When there was nothing
         to do at all, the cart phase 2 already holds is returned as it is. */
      var latest = cart;

      return ops.reduce(function (p, op) {
        return p.then(function () {
          if (op.type === 'remove') {
            return post(routes().change, { id: op.key, quantity: 0 })
              .then(function (c) { latest = c; });
          }
          if (op.type === 'change') {
            return post(routes().change, { id: op.key, quantity: op.quantity, properties: op.properties })
              .then(function (c) { latest = c; });
          }
          return post(routes().add, {
            items: [{ id: op.id, quantity: op.quantity, properties: op.properties }]
          }).then(function () { latest = null; });
        });
      }, Promise.resolve()).then(function () {
        return isCart(latest) ? latest : getCart();
      });
    });
  }

  /* ---------- selection : a last-intent-wins state machine ----------
     The cart is authoritative about STRUCTURE - which lines exist, what their
     group ids are, how many units a line may hold. It is NOT authoritative
     about the customer's INTENT while a request is in the air.

     Holding one object for each answers the bug this replaces: a reconcile
     takes several sequential round trips, and the answer to the FIRST tap used
     to be written straight over `desired`, so a second tap made during the
     flight was erased - on screen and, because the next run then re-read the
     erased value, in Shopify too.

     Here every response adopts the structure and only adopts the selection
     when the customer has not moved on (`seq` unchanged). If they have, their
     newer choice is kept and another run is asked for. No timers, no delays,
     no guessing: strictly ordered writes plus a version on the intent.

     It is deliberately DOM-free, so the page can paint it however it likes and
     so it can be exercised without a browser. */
  function clamp(units, max) {
    var n = Math.round(Number(units) || 0);
    if (!(n > 0)) return 0;
    var m = Math.max(0, Math.round(Number(max) || 0));
    return n > m ? m : n;
  }

  function Selection(cfg) {
    this.cfg = cfg;
    this.desired = {};    /* bind -> units the customer has asked for */
    this.confirmed = {};  /* bind -> units Shopify last confirmed      */
    this.max = {};        /* bind -> the product line's own quantity   */
    this.seq = 0;         /* bumped by every customer change           */
    this.syncing = false;
    this.lastError = null;
  }

  Selection.prototype.seed = function (entries) {
    var self = this;
    (entries || []).forEach(function (e) {
      var u = clamp(e.units, e.max);
      self.desired[e.bind] = u;
      self.confirmed[e.bind] = u;
      self.max[e.bind] = Math.max(0, Math.round(Number(e.max) || 0));
    });
    return this;
  };

  Selection.prototype.binds       = function ()  { return Object.keys(this.max); };
  Selection.prototype.maxOf       = function (b) { return Number(this.max[b] || 0); };
  Selection.prototype.unitsOf     = function (b) { return Number(this.desired[b] || 0); };
  Selection.prototype.confirmedOf = function (b) { return Number(this.confirmed[b] || 0); };
  /* A row is "saving" exactly while what the customer asked for differs from
     what the cart has confirmed - derived, never counted, so it can neither
     stick on nor be cleared early. */
  Selection.prototype.isSaving    = function (b) { return this.unitsOf(b) !== this.confirmedOf(b); };
  Selection.prototype.isDirty     = function () {
    var self = this;
    for (var i = 0, b = this.binds(); i < b.length; i++) if (self.isSaving(b[i])) return true;
    return false;
  };
  Selection.prototype.totalUnits  = function () {
    var self = this, n = 0;
    this.binds().forEach(function (b) { n += self.unitsOf(b); });
    return n;
  };

  /* Record a new customer intent. Returns the value actually taken (capped to
     the line quantity) so the caller can paint it without re-deriving. */
  Selection.prototype.set = function (bind, units) {
    var u = clamp(units, this.maxOf(bind));
    if (this.desired[bind] === u) return u;
    this.desired[bind] = u;
    this.seq++;
    this.lastError = null;
    return u;
  };

  /* Adopt everything the cart is entitled to decide, and nothing else.
     Returns { renames, gone } so the page can move a row's binding when a
     property write has exchanged its line key for a group id. */
  Selection.prototype.adopt = function (cart) {
    var self = this;
    var model = readModel(cart, this.cfg);
    var renames = {}, gone = [];
    var nextDesired = {}, nextConfirmed = {}, nextMax = {};

    this.binds().forEach(function (oldBind) {
      var bind = keyAlias[oldBind] || oldBind;
      var match = null;
      for (var i = 0; i < model.length; i++) {
        if (model[i].groupId === bind || model[i].key === bind) { match = model[i]; break; }
      }
      if (!match) { gone.push(oldBind); return; }

      var newBind = match.groupId || match.key;
      if (newBind !== oldBind) renames[oldBind] = newBind;

      nextMax[newBind] = match.quantity;
      nextConfirmed[newBind] = clamp(match.wrapQuantity, match.quantity);
      var intent = Object.prototype.hasOwnProperty.call(self.desired, oldBind)
        ? self.desired[oldBind]
        : match.wrapQuantity;
      nextDesired[newBind] = clamp(intent, match.quantity);
    });

    this.desired = nextDesired;
    this.confirmed = nextConfirmed;
    this.max = nextMax;
    return { renames: renames, gone: gone, model: model };
  };

  /* Take the cart's confirmed figures as the intent. */
  Selection.prototype.confirm = function () {
    var self = this, out = {};
    Object.keys(this.confirmed).forEach(function (b) { out[b] = self.confirmed[b]; });
    this.desired = out;
  };

  /* Revert ONCE to the last confirmed cart state, and say which rows moved, so
     the failure can be reported beside the product it belongs to. */
  Selection.prototype.revert = function () {
    var self = this, changed = [];
    this.binds().forEach(function (b) { if (self.isSaving(b)) changed.push(b); });
    this.confirm();
    return changed;
  };

  Selection.prototype.sync = function () {
    var self = this;
    var started = this.seq;
    this.syncing = true;
    return enqueue(function () {
      return reconcile(function () { return self.desired; }, self.cfg, 'apply');
    }).then(function (cart) {
      var r = self.adopt(cart);
      var superseded = self.seq !== started;
      if (!superseded) self.confirm();
      self.syncing = false;
      return { cart: cart, renames: r.renames, gone: r.gone, superseded: superseded };
    }, function (err) {
      self.syncing = false;
      self.lastError = err;
      throw err;
    });
  };

  /* ---------- bag editing on the checkout steps ----------
     editLine() changes ONE merchandise line's quantity (0 removes it) and then
     brings that line's own wrapping into line with the quantity Shopify
     actually accepted, following the recorded intent (see the header). It
     runs on the same queue as every wrapping write, so a bag edit, a wrapping
     toggle and a pre-checkout sweep can never interleave.

     The merchandise write and the wrapping write are separate requests, not a
     transaction. The result says honestly what happened:
       { cart, requested, accepted, previous, title, key, group,
         wrap:     null | { group, before, target, scope, ok, error },
         services: null | { ok, error }      (orphan clean-up after the last
                                              product was removed)
         stale:    true when the final re-read failed }
     A rejected or failed merchandise write rejects with an Error whose .kind
     is 'rejected' (HTTP 422, .message from Shopify), 'network', 'failed',
     'gone' (the targeted line no longer exists; .cart is the fresh cart) or
     'changed' (the line's quantity is no longer what the page showed). */
  function postRaw(url, body) {
    return timedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }, function (r) {
      return r.json().catch(function () { return null; })
        .then(function (d) { return { ok: r.ok, status: r.status, data: d }; });
    }).then(null, function () {
      return { ok: false, status: 0, data: null };
    });
  }

  function bagError(kind, extra) {
    var e = new Error(kind);
    e.kind = kind;
    Object.keys(extra || {}).forEach(function (k) { e[k] = extra[k]; });
    return e;
  }

  function isService(line, cfg) {
    return isWrapLine(line, cfg) || line.handle === cfg.premiumHandle;
  }

  function hasMerch(cart, cfg) {
    return ((cart && cart.items) || []).some(function (i) { return !isService(i, cfg); });
  }

  /* A line is targeted by its current key first and, failing that, by its
     wrapping group id - never by its position or its variant alone. */
  function resolveLine(items, ref, cfg) {
    var i;
    if (ref.key) {
      for (i = 0; i < items.length; i++) {
        if (items[i].key === ref.key && !isService(items[i], cfg)) return items[i];
      }
    }
    if (ref.group) {
      for (i = 0; i < items.length; i++) {
        if (!isService(items[i], cfg) && props(items[i])[GROUP_KEY] === ref.group) return items[i];
      }
    }
    return null;
  }

  function lineByKey(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
    return null;
  }

  /* Set the wrapping of one group to an ABSOLUTE number of units, so calling
     it twice can never add twice. Resolves with the updated cart, or null
     when nothing needed to change. Rejects when Shopify refuses, or accepts
     fewer units than asked. */
  function setWrap(cart, groupId, target, scope, productLine, cfg) {
    var items = (cart && cart.items) || [];
    var w = findByGroup(items, groupId, cfg, true);
    target = Math.max(0, Math.round(Number(target) || 0));

    function verify(c) {
      if (!isCart(c)) throw bagError('wrap', {});
      var now = findByGroup(c.items, groupId, cfg, true);
      var have = now ? now.quantity : 0;
      if (have !== target) throw bagError('wrap', { have: have, cart: c });
      return c;
    }

    if (!w) {
      if (target === 0) return Promise.resolve(null);
      if (!cfg.wrapVariantId || !productLine) return Promise.reject(bagError('wrap', {}));
      var p = {};
      p[GROUP_KEY] = groupId;
      p[LABEL_KEY] = wrapLabel(productLine);
      p[SCOPE_KEY] = scope || (target >= productLine.quantity ? 'all' : 'units');
      return post(routes().add, { items: [{ id: cfg.wrapVariantId, quantity: target, properties: p }] })
        .then(function () { return getCart(); })
        .then(verify);
    }
    if (target === 0) {
      return post(routes().change, { id: w.key, quantity: 0 }).then(verify);
    }
    var extra = {};
    extra[GROUP_KEY] = groupId;
    if (productLine) extra[LABEL_KEY] = wrapLabel(productLine);
    if (scope) extra[SCOPE_KEY] = scope;
    var unchanged = w.quantity === target && Object.keys(extra).every(function (k) { return props(w)[k] === extra[k]; });
    if (unchanged) return Promise.resolve(null);
    return post(routes().change, { id: w.key, quantity: target, properties: mergeProps(w, extra) }).then(verify);
  }

  /* After the last product has gone, any paid service left behind (wrapping,
     a premium box) is an orphan charge: remove each one, in sequence. */
  function clearServices(cart, cfg) {
    var lines = ((cart && cart.items) || []).filter(function (i) { return isService(i, cfg); });
    var latest = null;
    return lines.reduce(function (p, line) {
      return p.then(function () {
        return post(routes().change, { id: line.key, quantity: 0 }).then(function (c) { latest = c; });
      });
    }, Promise.resolve()).then(function () {
      if (latest && hasMerch(latest, cfg) === false && latest.items.length) throw bagError('services', {});
      return latest;
    });
  }

  function editLine(ref, qty, cfg) {
    qty = Math.max(0, Math.round(Number(qty) || 0));
    return getCart().then(null, function () { throw bagError('network', {}); }).then(function (cart) {
      var items = cart.items || [];
      var line = resolveLine(items, ref || {}, cfg);
      if (!line) throw bagError('gone', { cart: cart });
      if (typeof ref.expect === 'number' && ref.expect !== line.quantity) throw bagError('changed', { cart: cart });

      var group = props(line)[GROUP_KEY] || null;
      var wrap0 = group ? findByGroup(items, group, cfg, true) : null;
      var intent = wrap0 ? { units: wrap0.quantity, scope: scopeOf(wrap0, line) } : null;

      return postRaw(routes().change, { id: line.key, quantity: qty }).then(function (res) {
        if (!res.ok || !isCart(res.data)) {
          var kind = res.status === 422 ? 'rejected' : (res.status === 0 ? 'network' : 'failed');
          throw bagError(kind, {
            status: res.status,
            message: (res.data && (res.data.description || res.data.message)) || ''
          });
        }
        var after = lineByKey(res.data, line.key);
        var accepted = after ? after.quantity : 0;
        var out = {
          cart: res.data, requested: qty, accepted: accepted, previous: line.quantity,
          title: line.product_title || line.title, key: line.key, group: group,
          wrap: null, services: null, stale: false
        };

        var chain = Promise.resolve();
        if (intent) {
          /* Wrapping follows the ACCEPTED quantity, never the requested one. */
          var known = after ? eligibility(after, cfg) : false;
          var target = 0;
          if (accepted > 0 && known !== false) {
            target = intent.scope === 'all' ? accepted : Math.min(intent.units, accepted);
          }
          out.wrap = { group: group, before: intent.units, target: target, scope: intent.scope, ok: true, title: out.title };
          chain = chain.then(function () {
            return setWrap(out.cart, group, target, intent.scope, after, cfg);
          }).then(function (c) { if (isCart(c)) out.cart = c; }, function (err) {
            out.wrap.ok = false;
            out.wrap.error = err;
          });
        }
        chain = chain.then(function () {
          if (hasMerch(out.cart, cfg) || !(out.cart.items || []).length) return;
          return clearServices(out.cart, cfg).then(function (c) {
            if (isCart(c)) out.cart = c;
            out.services = { ok: true };
          }, function (err) { out.services = { ok: false, error: err }; });
        });
        return chain.then(function () {
          /* When a follow-up write failed, show what Shopify really holds. */
          if ((out.wrap && !out.wrap.ok) || (out.services && !out.services.ok)) {
            return getCart().then(function (c) { out.cart = c; }, function () { out.stale = true; });
          }
        }).then(function () { return out; });
      });
    });
  }

  /* Idempotent retry for a wrapping follow-up that failed: bring one group's
     wrapping to `target` units (capped to its product line), or remove it
     when the product line has gone. Resolves with the fresh cart. */
  function settleWrap(groupId, target, scope, cfg) {
    return getCart().then(function (cart) {
      var product = findByGroup(cart.items || [], groupId, cfg, false);
      var units = 0;
      if (product && eligibility(product, cfg) !== false) {
        units = Math.min(Math.max(0, Number(target) || 0), product.quantity);
      }
      return setWrap(cart, groupId, units, scope, product, cfg).then(function () { return getCart(); });
    });
  }

  /* ---------- serialised public API ---------- */
  var queue = Promise.resolve();
  var depth = 0;

  function enqueue(fn) {
    depth++;
    var run = queue.then(fn, fn);
    /* keep the chain alive after a rejection so one failure cannot wedge
       every later operation */
    queue = run.then(function () { depth--; }, function () { depth--; });
    return run;
  }

  window.CadooWrap = {
    GROUP_KEY: GROUP_KEY,
    LABEL_KEY: LABEL_KEY,
    SCOPE_KEY: SCOPE_KEY,
    getCart: getCart,
    scopeOf: scopeOf,
    hasMerch: hasMerch,
    /* True while any queued cart write (wrapping or bag edit) is pending. */
    busy: function () { return depth > 0; },
    /* Bag editing on the checkout steps - see editLine() above. */
    editLine: function (ref, qty, cfg) {
      return enqueue(function () { return editLine(ref, qty, cfg); });
    },
    settleWrap: function (groupId, target, scope, cfg) {
      return enqueue(function () { return settleWrap(groupId, target, scope, cfg); });
    },
    clearServices: function (cfg) {
      return enqueue(function () {
        return getCart().then(function (c) {
          if (hasMerch(c, cfg)) return c;
          return clearServices(c, cfg).then(function () { return getCart(); });
        });
      });
    },
    readModel: readModel,
    /* Resolve an identifier the page is still holding to the one the cart uses
       now. A row bound to a line's key before that line was given its first
       group id would otherwise stop matching the moment the id was written,
       because writing a property changes the key. */
    resolve: function (id) { return keyAlias[id] || id; },
    /* Apply the customer's selection. desired maps a group id (or, for a line
       that has none yet, that line's current key) to the number of units. */
    apply: function (desired, cfg) {
      return enqueue(function () { return reconcile(desired, cfg, 'apply'); });
    },
    /* Strip orphaned / ineligible wrapping and cap quantities. Safe to call on
       any page; it never adds a charge and never changes a product line. */
    cleanup: function (cfg) {
      return enqueue(function () { return reconcile({}, cfg, 'cleanup'); });
    },
    /* The last-intent-wins selection state machine used by the Gifting Details
       step. DOM-free on purpose: the page paints it, the tests drive it. */
    Selection: Selection,
    newSelection: function (cfg) { return new Selection(cfg); }
  };
})();
